import { useSelector } from "react-redux";
import { crm } from "../../../store/cabinetApi/crm";
import { 
  CompanyCategory,
  ContactFieldTemplate,
  Tag,
  RelationshipTag,
} from "../../../store/cabinetApi/generated/crm";
import CRM from "./CRM";
import { RootState } from "../../../store";
import useContactsList from "../../../components/CRM/CRMContacts/contactsListHook";
import { useState } from "react";


const CRMSettingsContainer = ({ subTab }: { subTab?: string }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [contactSearchQuery, setContactSearchQuery] = useState("");

  const { data: fieldTemplatesData } = crm.useAppCrmContactFieldTemplatesListQuery({ limit: 1000 });
  const { data: leaderPrivateContacts, isLoading: isLoadingPrivateContacts } = crm.useAppCrmPrivateContactsListQuery();
  const [createFieldTemplate] = crm.useAppCrmContactFieldTemplatesCreateMutation();
  const [updateFieldTemplate] = crm.useAppCrmContactFieldTemplatesPartialUpdateMutation();
  const [createFieldTemplateChoice] = crm.useAppCrmContactFieldChoicesCreateMutation();
  const [deleteFieldTemplate] = crm.useAppCrmContactFieldTemplatesDestroyMutation();
  const [addPrivateContact] = crm.useAppCrmPrivateContactsAddPrivateContactCreateMutation();
  const [removePrivateContact] = crm.useAppCrmPrivateContactsRemovePrivateContactCreateMutation();
  const contactsListData = useContactsList({ pageSize: 10, search: contactSearchQuery });


  const leaders = useSelector((root: RootState) => root.leaders.leaders);

  const [
    createCompanyCategory,
    { isLoading: isCreatingContactOrgCategory }
  ] = crm.useAppCrmCompanyCategoriesCreateMutation();
  const [
    updateCompanyCategory,
    { isLoading: isUpdatingContactOrgCategory }
  ] = crm.useAppCrmCompanyCategoriesPartialUpdateMutation();
  const [
    deleteCompanyCategory,
    { isLoading: isDeletingContactOrgCategory }
  ] = crm.useAppCrmCompanyCategoriesDestroyMutation();
  const {data} = crm.useAppCrmCompanyCategoriesListQuery({
    limit: 100,
  });
  
  const organizationContactCategories: {[key: number]: CompanyCategory} = Object.fromEntries(
    data?.data?.map(
      (category) => [category.id, category]
    )  || []
  );

  const [
    createTag,
    { isLoading: isCreatingTag }
  ] = crm.useAppCrmContactTagsCreateMutation();
  const [
    updateTag,
    { isLoading: isUpdatingTag }
  ] = crm.useAppCrmContactTagsPartialUpdateMutation();
  const [
    deleteTag,
    { isLoading: isDeletingTag }
  ] = crm.useAppCrmContactTagsDestroyMutation();
  const {data: tagsData} = crm.useAppCrmContactTagsListQuery({
    limit: 100,
  });
  
  const organizationTags: {[key: number]: Tag} = Object.fromEntries(
    tagsData?.data?.map(
      (tag) => [tag.id, tag]
    )  || []
  );

  const [
    createRelationshipTag,
    { isLoading: isCreatingRelationshipTag }
  ] = crm.useAppCrmRelationshipTagsCreateMutation();
  const [
    updateRelationshipTag,
    { isLoading: isUpdatingRelationshipTag }
  ] = crm.useAppCrmRelationshipTagsPartialUpdateMutation();
  const [
    deleteRelationshipTag,
    { isLoading: isDeletingRelationshipTag }
  ] = crm.useAppCrmRelationshipTagsDestroyMutation();
  const {data: relationshipTagsData} = crm.useAppCrmRelationshipTagsListQuery({
    limit: 100,
  });
  
  const organizationRelationshipTags: {[key: number]: RelationshipTag} = Object.fromEntries(
    relationshipTagsData?.data?.map(
      (tag) => [tag.id, tag]
    )  || []
  );

  
  const handleAddFieldTemplate = async (contact: ContactFieldTemplate) => {
    const res = await createFieldTemplate(contact);
    if (contact.contact_field_choices.length && res.data && "id" in res.data) {
      // TODO: update this logic when backend support bulk endpoints
      const choicesPromise = contact.contact_field_choices.map(choice => (
        createFieldTemplateChoice({
          ...choice,
          contact_field_template: res.data.id,
        })
      ));
      await Promise.all(choicesPromise);
    }

    return res;
  };

  const handleCreateCompanyCategory = async (category: CompanyCategory) => {
    createCompanyCategory(category);
  };

  const handleUpdateCompanyCategory = async (category: CompanyCategory) => {
    updateCompanyCategory({id: category.id, patchedCompanyCategory: category});
  };

  const handleDeleteCompanyCategory = async (category: CompanyCategory) => {
    deleteCompanyCategory(category.id);
  };

  const handleCreateTag = async (tag: Tag) => {
    createTag({ name: tag.name, color: tag.color });
  };

  const handleUpdateTag = async (tag: Tag) => {
    updateTag({id: tag.id, patchedTag: { name: tag.name, color: tag.color }});
  };

  const handleDeleteTag = async (tag: Tag) => {
    deleteTag(tag.id);
  };

  const handleCreateRelationshipTag = async (tag: RelationshipTag) => {
    createRelationshipTag({ name: tag.name, color: tag.color });
  };

  const handleUpdateRelationshipTag = async (tag: RelationshipTag) => {
    updateRelationshipTag({id: tag.id, patchedRelationshipTag: { name: tag.name, color: tag.color }});
  };

  const handleDeleteRelationshipTag = async (tag: RelationshipTag) => {
    deleteRelationshipTag(tag.id);
  };

  const handleAddPrivateContact = async (leaderId: number, contactId: number) => {
    addPrivateContact({ id: leaderId, addRemovePrivateContactInputRequest: { contactId } });
  };

  const handleRemovePrivateContact = async (leaderId: number, contactId: number) => {
    removePrivateContact({ id: leaderId, addRemovePrivateContactInputRequest: { contactId } });
  };

  const handleSearchContacts = async (query: string) => {
    setContactSearchQuery(query);
  };

  return (
    <CRM
      fieldTemplates={fieldTemplatesData?.data || []}
      onAddFieldTemplate={handleAddFieldTemplate}
      onUpdateFieldTemplate={updateFieldTemplate}
      onDeleteFieldTemplate={deleteFieldTemplate}
      isUpdatingContactOrgCategory={isUpdatingContactOrgCategory}
      isCreatingContactOrgCategory={isCreatingContactOrgCategory}
      isDeletingContactOrgCategory={isDeletingContactOrgCategory}
      user={user}
      organizationContactCategories={organizationContactCategories}
      createCompanyCategory={handleCreateCompanyCategory}
      updateCompanyCategory={handleUpdateCompanyCategory}
      deleteCompanyCategory={handleDeleteCompanyCategory}
      organizationTags={organizationTags}
      isUpdatingTag={isUpdatingTag}
      isCreatingTag={isCreatingTag}
      isDeletingTag={isDeletingTag}
      createTag={handleCreateTag}
      updateTag={handleUpdateTag}
      deleteTag={handleDeleteTag}
      leaderPrivateContacts={leaderPrivateContacts || []}
      leaders={leaders}
      foundContacts={contactSearchQuery ? contactsListData.contacts : []}
      onSearchContacts={handleSearchContacts}
      onAddPrivateContact={handleAddPrivateContact}
      onRemovePrivateContact={handleRemovePrivateContact}
      isLoadingPrivateContacts={isLoadingPrivateContacts}
      subTab={subTab}
      organizationRelationshipTags={organizationRelationshipTags}
      isUpdatingRelationshipTag={isUpdatingRelationshipTag}
      isCreatingRelationshipTag={isCreatingRelationshipTag}
      isDeletingRelationshipTag={isDeletingRelationshipTag}
      createRelationshipTag={handleCreateRelationshipTag}
      updateRelationshipTag={handleUpdateRelationshipTag}
      deleteRelationshipTag={handleDeleteRelationshipTag}
    />
  );
};

export default CRMSettingsContainer;
